import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { GlobalContext } from "../../Context/GlobalContext";
import { Toaster, toast } from "react-hot-toast";
import {
  RefreshCcw,
  Search,
  Calendar,
  Building2,
  Settings2,
  Receipt,
  Percent,
  Truck,
  Wrench,
  CreditCard,
} from "lucide-react";
import Customization from "./Pos/Customization";
import OtherPaymentMethod from "./Pos/OtherPaymentMethod";
import useOrders from "../Services/OrderServices/useOrders";
import ClientSearchSelect from "../common/ClientSearchSelect";

/** small utils */
const cls = (...s) => s.filter(Boolean).join(" ");
const fmt2 = (n) => {
  const x = Number(n);
  return Number.isFinite(x) ? x.toFixed(2) : "0.00";
};

export default function AdminPosOrders() {
  const {
    getPosOrders,
    getPosDiscountList,
    updatePosDiscount,
    deletePosDiscount,
    getPosPackkyDeliveryCharges,
    insertPosPackkyDeliveryCharges,
    getReceiptData,
    updatePrintSettings,
  } = useOrders();

  const { api, AllClients = [] } = useContext(GlobalContext);

  // client selector
  const [userID, setUserID] = useState("");
  const [clientName, setClientName] = useState("");

  // dates (defaults to today)
  const todayISO = useMemo(() => new Date().toISOString().split("T")[0], []);
  const [fromDate, setFromDate] = useState(todayISO);
  const [toDate, setToDate] = useState(todayISO);

  // data
  const [orders, setOrders] = useState([]);
  const [initialLoading, setInitialLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // paging + guards
  const pageIndexRef = useRef(1);            // starts at 1
  const recPerPageRef = useRef(50);          // fixed 50
  const fetchingRef = useRef(false);         // prevent double fetch
  const lastKeyRef = useRef("");
  const lastSettingsKeyRef = useRef("");             // guard against stale responses
  const [activeMainTab, setActiveMainTab] = useState("orders");
  const [activeSettingsTab, setActiveSettingsTab] = useState("deliveryCharges");

  // settings data
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);

  const [discountsList, setDiscountsList] = useState([]);
  const [chargesData, setChargesData] = useState([]);
  const [printSettingDetails, setPrintSettingDetails] = useState(null);
  const [deliveryChargeForm, setDeliveryChargeForm] = useState({
    amount: "",
  });

  const [deliveryChargeSaving, setDeliveryChargeSaving] = useState(false);
  const [discountForm, setDiscountForm] = useState({
    discount: "",
    discountType: "",
    orderType: "",
  });

  const [printSettingForm, setPrintSettingForm] = useState({
    normalFontSize: "",
    largeFontSize: "",
    pageWidth: "",
    anlookActive: false,
    autoPrint: false,
  });
  // scroll container
  const scrollRef = useRef(null);

  /** dedupe by orderid when appending */
  const appendUnique = (curr, incoming) => {
    const seen = new Set(curr.map((o) => o.orderid));
    const add = incoming.filter((o) => !seen.has(o.orderid));
    return curr.concat(add);
  };
  const settingsTabs = [
    { key: "deliveryCharges", label: "Delivery Charges", icon: Truck },
    { key: "discounts", label: "Discounts", icon: Percent },
    { key: "customization", label: "Customization", icon: Wrench },
    { key: "paymentmethod", label: "Payment Method", icon: CreditCard },
    { key: "printing", label: "Receipt Printing", icon: Receipt },
  ];

  const loadSettingsTabData = async (tabKey = activeSettingsTab, force = false) => {
    if (!userID || settingsLoading) return;

    const settingsKey = `${userID}|${tabKey}`;
    if (!force && lastSettingsKeyRef.current === settingsKey) return;

    lastSettingsKeyRef.current = settingsKey;
    setSettingsLoading(true);

    try {
      if (tabKey === "deliveryCharges") {
        const res = await getPosPackkyDeliveryCharges(userID);
        if (res.success) {
          setChargesData(res.data || []);
        } else {
          setChargesData([]);
          toast.error(res.message);
        }
      }

      if (tabKey === "discounts") {
        const res = await getPosDiscountList(userID);
        if (res.success) {
          setDiscountsList(res.data || []);
        } else {
          setDiscountsList([]);
          toast.error(res.message);
        }
      }

      if (tabKey === "printing") {
        const res = await getReceiptData(userID);
        if (res.success) {
          setPrintSettingDetails(res.data || null);

          const settingsArr = Array.isArray(res?.data?.printSettings)
            ? res.data.printSettings
            : [];

          setPrintSettingForm({
            normalFontSize:
              settingsArr.find((x) => x.key === "normalFontSize")?.value || "",
            largeFontSize:
              settingsArr.find((x) => x.key === "largeFontSize")?.value || "",
            pageWidth:
              settingsArr.find((x) => x.key === "pageWidth")?.value || "",
            anlookActive:
              settingsArr.find((x) => x.key === "anlookActive")?.value === "1",
            autoPrint:
              settingsArr.find((x) => x.key === "autoPrint")?.value === "1",
          });
        } else {
          setPrintSettingDetails(null);
          toast.error(res.message);
        }
      }

      setSettingsLoaded(true);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load settings.");
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleDiscountFormChange = (e) => {
    const { name, value } = e.target;
    setDiscountForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePrintFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPrintSettingForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };
  const handleDeliveryChargeFormChange = (e) => {
    const { name, value } = e.target;

    // allow only digits and decimal
    if (name === "amount" && value !== "" && !/^\d*\.?\d*$/.test(value)) return;

    setDeliveryChargeForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveDeliveryCharges = async () => {
    if (!userID) return toast.error("Please select a client first.");

    const amount = String(deliveryChargeForm.amount || "").trim();

    if (!amount) {
      return toast.error("Please enter delivery charges amount.");
    }

    if (Number.isNaN(Number(amount)) || Number(amount) < 0) {
      return toast.error("Please enter a valid delivery charges amount.");
    }

    setDeliveryChargeSaving(true);
    try {
      const res = await insertPosPackkyDeliveryCharges({
        fkuserid: userID,
        amount,
      });

      if (res.success) {
        toast.success(res.message);
        setDeliveryChargeForm({ amount: "" });
        await loadSettingsTabData("deliveryCharges", true);
      } else {
        toast.error(res.message);
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to update delivery charges.");
    } finally {
      setDeliveryChargeSaving(false);
    }
  };
  const handleSaveDiscount = async () => {
    if (!userID) return toast.error("Please select a client first.");
    if (!discountForm.discount || !discountForm.discountType || !discountForm.orderType) {
      return toast.error("Please fill all discount fields.");
    }

    const res = await updatePosDiscount({
      userid: userID,
      discount: discountForm.discount,
      discountType: discountForm.discountType,
      orderType: discountForm.orderType,
    });

    if (res.success) {
      toast.success(res.message);
      setDiscountForm({
        discount: "",
        discountType: "",
        orderType: "",
      });
      loadSettingsTabData("discounts");
    } else {
      toast.error(res.message);
    }
  };

  const handleDeleteDiscount = async (discountId) => {
    const res = await deletePosDiscount({ userid: userID, discountId });
    if (res.success) {
      toast.success(res.message);
      loadSettingsTabData("discounts");
    } else {
      toast.error(res.message);
    }
  };

  const handleSavePrintSettings = async () => {
    if (!userID) return toast.error("Please select a client first.");
    if (
      !printSettingForm.normalFontSize ||
      !printSettingForm.largeFontSize ||
      !printSettingForm.pageWidth
    ) {
      return toast.error("Please fill all printing fields.");
    }

    const res = await updatePrintSettings({
      userid: userID,
      printConfig: [
        { key: "normalFontSize", value: String(printSettingForm.normalFontSize) },
        { key: "largeFontSize", value: String(printSettingForm.largeFontSize) },
        { key: "pageWidth", value: String(printSettingForm.pageWidth) },
        { key: "anlookActive", value: printSettingForm.anlookActive ? "1" : "0" },
        { key: "autoPrint", value: printSettingForm.autoPrint ? "1" : "0" },
      ],
    });

    if (res.success) {
      toast.success(res.message);
      loadSettingsTabData("printing");
    } else {
      toast.error(res.message);
    }
  };
  /** build context key for guarding stale appends if filters changed mid-flight */
  const buildCtxKey = useCallback(
    () => `${userID}|${fromDate}|${toDate}`,
    [userID, fromDate, toDate]
  );

  /** core loader for a page */
  const fetchOrdersPage = async ({ isFirst = false, ctxKey }) => {
    if (!userID || fetchingRef.current || !hasMore) return;

    fetchingRef.current = true;
    setLoading(true);

    try {
      const payload = {
        userid: userID,
        status: "hold,completed,open,closed",
        fromdate: fromDate,
        todate: toDate,
        pageIndex: String(pageIndexRef.current),
        recordsPerPage: String(recPerPageRef.current),
      };

      const result = await getPosOrders(payload);

      if (result.success) {
        const list = result.data;

        // discard stale responses if filters changed
        if (ctxKey && ctxKey !== lastKeyRef.current) return;

        setOrders((prev) => appendUnique(prev, list));

        if (list.length < recPerPageRef.current) {
          setHasMore(false);
        } else {
          pageIndexRef.current += 1;
        }
      } else {
        if (isFirst) setOrders([]);
        setHasMore(false);
        toast.error(result.message);
      }
    } catch (e) {
      if (isFirst) setOrders([]);
      setHasMore(false);
      toast.error("Error fetching orders.");
      console.error(e);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  };

  /** reset and load first page */
  const resetAndLoad = useCallback(async () => {
    if (!userID) return toast.info("Please select a client first.");
    // context key
    const key = buildCtxKey();
    lastKeyRef.current = key;

    // reset
    setOrders([]);
    setHasMore(true);
    pageIndexRef.current = 1;

    // scroll to top
    if (scrollRef.current) scrollRef.current.scrollTop = 0;

    setInitialLoading(true);
    await fetchOrdersPage({ isFirst: true, ctxKey: key });
    setInitialLoading(false);
  }, [userID, buildCtxKey]);

  /** infinite scroll handler */
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || loading || !hasMore) return;
    const threshold = 150; // px from bottom
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
    if (nearBottom) {
      const key = lastKeyRef.current;
      fetchOrdersPage({ ctxKey: key });
    }
  }, [loading, hasMore]);

  // attach/detach scroll
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => handleScroll();
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [handleScroll]);

  /** manual refresh (keeps filters) */
  const refresh = async () => {
    await resetAndLoad();
  };

  /** when client selection changes from dropdown */
  const onClientChange = (e) => {
    const val = e.target.value;

    setOrders([]);
    setHasMore(false);

    setDiscountsList([]);
    setChargesData([]);
    setPrintSettingDetails(null);

    setDiscountForm({
      discount: "",
      discountType: "",
      orderType: "",
    });

    setPrintSettingForm({
      normalFontSize: "",
      largeFontSize: "",
      pageWidth: "",
      anlookActive: false,
      autoPrint: false,
    });
    setDeliveryChargeForm({
      amount: "",
    });
    setSettingsLoaded(false);
    lastSettingsKeyRef.current = "";

    if (!val) {
      setUserID("");
      setClientName("");
      return;
    }

    const { code, name } = JSON.parse(val);
    setUserID(code);
    setClientName(name);
  };
  useEffect(() => {
    if (!userID || activeMainTab !== "settings") return;
    loadSettingsTabData(activeSettingsTab);
  }, [activeMainTab, activeSettingsTab, userID]);
  return (
    <div className="p-3 lg:p-5">
      <Toaster position="top-right" />

      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#01C37D]/10 text-[#01C37D]">
            <Building2 size={18} />
          </div>
          <div>
            <h1 className="text-xl font-semibold">POS Orders</h1>
            <p className="text-sm text-gray-500">Manage & view orders from Anlook POS</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-xl border bg-white p-1">
            <button
              onClick={() => setActiveMainTab("orders")}
              className={cls(
                "rounded-lg px-3 py-1.5 text-sm font-medium",
                activeMainTab === "orders"
                  ? "bg-[#01C37D] text-white"
                  : "text-gray-600 hover:bg-gray-50"
              )}
            >
              Orders
            </button>
            <button
              onClick={() => setActiveMainTab("settings")}
              className={cls(
                "rounded-lg px-3 py-1.5 text-sm font-medium",
                activeMainTab === "settings"
                  ? "bg-[#01C37D] text-white"
                  : "text-gray-600 hover:bg-gray-50"
              )}
            >
              Settings
            </button>
          </div>

          <button
            onClick={
              activeMainTab === "orders"
                ? refresh
                : () => loadSettingsTabData(activeSettingsTab, true)
            }
            disabled={initialLoading || loading || settingsLoading}
            className="inline-flex h-9 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50"
          >
            <RefreshCcw size={16} />
            {initialLoading || loading || settingsLoading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {/* Card: Client select */}
      <div className="rounded-2xl border bg-white p-3">
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <div className="mb-1 text-xs text-gray-500">Select Restaurant / Client</div>
            <ClientSearchSelect
              clients={AllClients || []}
              value={userID}
              className="w-full"
              placeholder={
                AllClients?.length ? "Select Client…" : "Loading clients…"
              }
              onChange={(client) => {
                setUserID(client?.us_code || "");
                setClientName(client?.us_name || "");
              }}
            />
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <label className="text-sm">
              <div className="mb-1 flex items-center gap-1 text-xs text-gray-500">
                <Calendar size={14} /> From
              </div>
              <input
                type="date"
                className="h-10 w-full rounded-xl border px-3 text-sm outline-none"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </label>
            <label className="text-sm">
              <div className="mb-1 flex items-center gap-1 text-xs text-gray-500">
                <Calendar size={14} /> To
              </div>
              <input
                type="date"
                className="h-10 w-full rounded-xl border px-3 text-sm outline-none"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </label>
            {activeMainTab === "orders" ? (
              <div className="flex items-end">
                <button
                  onClick={resetAndLoad}
                  disabled={!userID || initialLoading || loading}
                  className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-[#01C37D] px-4 text-sm font-medium text-white hover:bg-[#01C37D]/90 disabled:opacity-60"
                >
                  <Search size={16} />
                  {initialLoading || loading ? "Loading…" : "Search"}
                </button>
              </div>
            ) : (
              <div className="flex items-end">
                <button
                  onClick={() => loadSettingsTabData(activeSettingsTab, true)}
                  disabled={!userID || settingsLoading}
                  className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-[#01C37D] px-4 text-sm font-medium text-white hover:bg-[#01C37D]/90 disabled:opacity-60"
                >
                  <Settings2 size={16} />
                  {settingsLoading ? "Loading…" : "Load Settings"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Orders Table */}
      {userID && activeMainTab === "orders" && (
        <div className="mt-3 rounded-2xl border bg-white">
          <div className="flex items-center justify-between border-b px-3 py-2">
            <div className="text-sm font-semibold">
              POS Orders History {clientName ? `of ${clientName}` : ""}
            </div>
          </div>

          <div
            ref={scrollRef}
            className="max-h-[68vh] overflow-auto"
          >
            <table className="min-w-full table-fixed text-left text-sm">
              <colgroup>
                <col className="w-[16%]" />
                <col className="w-[12%]" />
                <col className="w-[20%]" />
                <col className="w-[14%]" />
                <col className="w-[14%]" />
                <col className="w-[12%]" />
                <col className="w-[12%]" />
              </colgroup>
              <thead className="bg-[#01C37D] text-white sticky top-0 z-10">
                <tr className="whitespace-nowrap">
                  {["Order ID", "Date", "Customer", "Mobile", "Type", "Amount", "Due"].map((h) => (
                    <th key={h} className="px-3 py-2 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="align-top">
                {/* initial empty state */}
                {initialLoading && orders.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-6 text-center text-gray-500">Loading first page…</td>
                  </tr>
                )}

                {/* rows */}
                {orders.length > 0 && orders.map((o) => {
                  const due = String(o?.payment_status).toLowerCase() === "paid" ? 0 : Number(o?.totalOrderPrice || 0);
                  return (
                    <tr key={o.orderid} className="border-t">
                      <td className="px-3 py-2">
                        {(o.daily_order_id || "HOLD")} / {o.orderid}
                      </td>
                      <td className="px-3 py-2">{o.order_date || "—"}</td>
                      <td className="px-3 py-2">{o.customer_name || "N/A"}</td>
                      <td className="px-3 py-2">{o.customer_mobile || "-"}</td>
                      <td className="px-3 py-2">
                        {String(o.orderType || "").toUpperCase()}
                        {o.orderType === "dining" && o.table_no ? ` · Table ${o.table_no}` : ""}
                      </td>
                      <td className="px-3 py-2">₹ {fmt2(o.totalOrderPrice)}</td>
                      <td className="px-3 py-2">
                        <span
                          className={cls(
                            "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold",
                            due === 0
                              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                              : "border-rose-200 bg-rose-50 text-rose-700"
                          )}
                        >
                          ₹ {fmt2(due)}
                        </span>
                      </td>
                    </tr>
                  );
                })}

                {/* no data after loaded */}
                {!initialLoading && orders.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-6 text-center text-gray-500">
                      No orders found for selected filters.
                    </td>
                  </tr>
                )}

                {/* loading more */}
                {loading && orders.length >= 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-4 text-center text-gray-500">Loading more…</td>
                  </tr>
                )}

                {/* end reached */}
                {!loading && orders.length >= 0 && !hasMore && (
                  <tr>
                    <td colSpan={7} className="px-4 py-4 text-center text-gray-500">End of results.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pos Order Setting */}

      {userID && activeMainTab === "settings" && (
        <div className="mt-3 rounded-2xl border bg-white">
          <div className="border-b px-3 py-3">
            <div className="mb-3 text-sm font-semibold">
              POS Order Settings {clientName ? `of ${clientName}` : ""}
            </div>

            <div className="flex flex-wrap gap-2">
              {settingsTabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveSettingsTab(tab.key)}
                    className={cls(
                      "inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium",
                      activeSettingsTab === tab.key
                        ? "border-[#01C37D] bg-[#01C37D]/10 text-[#01C37D]"
                        : "border-gray-200 text-gray-600 hover:bg-gray-50"
                    )}
                  >
                    <Icon size={15} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="p-4">
            {settingsLoading ? (
              <div className="py-10 text-center text-sm text-gray-500">Loading settings…</div>
            ) : (
              <>
                {activeSettingsTab === "deliveryCharges" && (
                  <div className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
                    <div className="space-y-3">
                      {chargesData.length > 0 ? (
                        chargesData.map((charge, index) => (
                          <div key={index} className="rounded-2xl border p-4">
                            <div className="mb-3 border-b pb-2 text-sm font-semibold text-[#01C37D]">
                              Current Delivery Charges
                            </div>

                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between gap-3">
                                <span className="text-gray-500">Delivery Charges</span>
                                <span className="font-medium">₹ {fmt2(charge.delivery_charges)}</span>
                              </div>

                              <div className="flex justify-between gap-3">
                                <span className="text-gray-500">Status</span>
                                <span
                                  className={cls(
                                    "font-medium",
                                    charge.status === "Y" ? "text-emerald-600" : "text-rose-600"
                                  )}
                                >
                                  {charge.status === "Y" ? "Active" : "Inactive"}
                                </span>
                              </div>

                              <div className="flex justify-between gap-3">
                                <span className="text-gray-500">Date</span>
                                <span className="font-medium">{charge.indate || "-"}</span>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="rounded-2xl border border-dashed p-6 text-sm text-gray-500">
                          No delivery charges found.
                        </div>
                      )}
                    </div>

                    <div className="rounded-2xl border bg-gray-50 p-4">
                      <div className="mb-4 text-sm font-semibold text-[#01C37D]">
                        Update Delivery Charges
                      </div>

                      <div className="grid gap-3">
                        <div>
                          <div className="mb-1 text-xs text-gray-500">Delivery Charges Amount</div>
                          <input
                            type="text"
                            name="amount"
                            value={deliveryChargeForm.amount}
                            onChange={handleDeliveryChargeFormChange}
                            className="h-10 w-full rounded-xl border bg-white px-3 text-sm outline-none"
                            placeholder="Enter amount"
                            disabled={deliveryChargeSaving}
                          />
                        </div>

                        <button
                          onClick={handleSaveDeliveryCharges}
                          disabled={deliveryChargeSaving}
                          className="mt-2 inline-flex h-10 items-center justify-center rounded-xl bg-[#01C37D] px-4 text-sm font-medium text-white hover:bg-[#01C37D]/90 disabled:opacity-60"
                        >
                          {deliveryChargeSaving ? "Saving…" : "Save Delivery Charges"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {activeSettingsTab === "discounts" && (
                  <div className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
                    <div className="space-y-3">
                      {discountsList.length > 0 ? (
                        discountsList.map((discount, index) => (
                          <div key={index} className="rounded-2xl border p-4">
                            <div className="mb-3 flex items-center justify-between border-b pb-2">
                              <div className="text-sm font-semibold text-[#01C37D]">
                                Currently Applied Discount
                              </div>
                              <button
                                onClick={() => handleDeleteDiscount(discount.id)}
                                className="rounded-lg bg-rose-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-600"
                              >
                                Remove
                              </button>
                            </div>

                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between gap-3">
                                <span className="text-gray-500">Discount</span>
                                <span className="font-medium">
                                  {discount.discountType === "fixed" ? "₹" : ""}
                                  {fmt2(discount.discount)}
                                  {discount.discountType === "percentage" ? "%" : ""}
                                </span>
                              </div>
                              <div className="flex justify-between gap-3">
                                <span className="text-gray-500">Discount Type</span>
                                <span className="font-medium">{discount.discountType}</span>
                              </div>
                              <div className="flex justify-between gap-3">
                                <span className="text-gray-500">Order Type</span>
                                <span className="font-medium">{discount.orderType}</span>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="rounded-2xl border border-dashed p-6 text-sm text-gray-500">
                          No discounts found.
                        </div>
                      )}
                    </div>

                    <div className="rounded-2xl border bg-gray-50 p-4">
                      <div className="mb-4 text-sm font-semibold text-[#01C37D]">Update Discount</div>

                      <div className="grid gap-3">
                        <div>
                          <div className="mb-1 text-xs text-gray-500">Discount</div>
                          <input
                            type="text"
                            name="discount"
                            value={discountForm.discount}
                            onChange={handleDiscountFormChange}
                            className="h-10 w-full rounded-xl border bg-white px-3 text-sm outline-none"
                            placeholder="Enter discount"
                          />
                        </div>

                        <div>
                          <div className="mb-1 text-xs text-gray-500">Discount Type</div>
                          <select
                            name="discountType"
                            value={discountForm.discountType}
                            onChange={handleDiscountFormChange}
                            className="h-10 w-full rounded-xl border bg-white px-3 text-sm outline-none"
                          >
                            <option value="">Select Discount Type</option>
                            <option value="fixed">Fixed</option>
                            <option value="percentage">Percentage</option>
                          </select>
                        </div>

                        <div>
                          <div className="mb-1 text-xs text-gray-500">Order Type</div>
                          <select
                            name="orderType"
                            value={discountForm.orderType}
                            onChange={handleDiscountFormChange}
                            className="h-10 w-full rounded-xl border bg-white px-3 text-sm outline-none"
                          >
                            <option value="">Select Order Type</option>
                            <option value="homeDelivery">Home Delivery</option>
                            <option value="takeaway">Takeaway</option>
                            <option value="dining">Dining</option>
                          </select>
                        </div>

                        <button
                          onClick={handleSaveDiscount}
                          className="mt-2 inline-flex h-10 items-center justify-center rounded-xl bg-[#01C37D] px-4 text-sm font-medium text-white hover:bg-[#01C37D]/90"
                        >
                          Save Discount
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {activeSettingsTab === "printing" && (
                  <div className="grid gap-4 xl:grid-cols-[1fr_1fr]">
                    <div className="space-y-4">
                      <div className="rounded-2xl border p-4">
                        <div className="mb-3 border-b pb-2 text-sm font-semibold text-[#01C37D]">
                          Current Print Settings
                        </div>

                        {printSettingDetails ? (
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between gap-3">
                              <span className="text-gray-500">Anlook Printing Software</span>
                              <span className="font-medium">
                                {printSettingDetails?.printSettings?.find((p) => p.key === "anlookActive")?.value === "1"
                                  ? "Enabled"
                                  : "Disabled"}
                              </span>
                            </div>

                            <div className="flex justify-between gap-3">
                              <span className="text-gray-500">Auto Print</span>
                              <span className="font-medium">
                                {printSettingDetails?.printSettings?.find((p) => p.key === "autoPrint")?.value === "1"
                                  ? "Enabled"
                                  : "Disabled"}
                              </span>
                            </div>

                            {(printSettingDetails?.printSettings || [])
                              .filter((x) =>
                                ["normalFontSize", "largeFontSize", "pageWidth"].includes(x.key)
                              )
                              .map((setting) => (
                                <div key={setting.key} className="flex justify-between gap-3">
                                  <span className="text-gray-500">{setting.key}</span>
                                  <span className="font-medium">{setting.value}</span>
                                </div>
                              ))}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-500">No print setting data found.</div>
                        )}
                      </div>

                      <div className="rounded-2xl border bg-gray-50 p-4">
                        <div className="mb-4 text-sm font-semibold text-[#01C37D]">Update Printing Setting</div>

                        <div className="grid gap-3">
                          <div>
                            <div className="mb-1 text-xs text-gray-500">Normal Font Size (PX)</div>
                            <input
                              type="text"
                              name="normalFontSize"
                              value={printSettingForm.normalFontSize}
                              onChange={handlePrintFormChange}
                              className="h-10 w-full rounded-xl border bg-white px-3 text-sm outline-none"
                            />
                          </div>

                          <div>
                            <div className="mb-1 text-xs text-gray-500">Large Font Size (PX)</div>
                            <input
                              type="text"
                              name="largeFontSize"
                              value={printSettingForm.largeFontSize}
                              onChange={handlePrintFormChange}
                              className="h-10 w-full rounded-xl border bg-white px-3 text-sm outline-none"
                            />
                          </div>

                          <div>
                            <div className="mb-1 text-xs text-gray-500">Page Width (MM)</div>
                            <input
                              type="text"
                              name="pageWidth"
                              value={printSettingForm.pageWidth}
                              onChange={handlePrintFormChange}
                              className="h-10 w-full rounded-xl border bg-white px-3 text-sm outline-none"
                            />
                          </div>

                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              name="anlookActive"
                              checked={printSettingForm.anlookActive}
                              onChange={handlePrintFormChange}
                            />
                            Activate Anlook Printing Software
                          </label>

                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              name="autoPrint"
                              checked={printSettingForm.autoPrint}
                              onChange={handlePrintFormChange}
                            />
                            Enable Auto Printing
                          </label>

                          <button
                            onClick={handleSavePrintSettings}
                            className="mt-2 inline-flex h-10 items-center justify-center rounded-xl bg-[#01C37D] px-4 text-sm font-medium text-white hover:bg-[#01C37D]/90"
                          >
                            Save Printing Settings
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-2xl border bg-gray-50 p-4">
                      <div className="mb-3 text-base font-semibold">Anlook Auto Printing Software</div>
                      <div className="rounded-2xl border bg-white p-4">
                        <a
                          href="https://drive.google.com/drive/folders/1e6_CaxQnLNx0D96IqAlKFEc0erspm_IK?usp=sharing"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex rounded-xl bg-[#01C37D] px-4 py-2 text-sm font-semibold text-white hover:bg-[#01C37D]/90"
                        >
                          Download Now
                        </a>

                        <div className="mt-4 space-y-3 text-sm text-gray-600">
                          <div>1. Download the software zip.</div>
                          <div>2. Extract the zip file.</div>
                          <div>3. Run <span className="font-semibold">AnlookAutoPrint.exe</span>.</div>
                          <div>4. Keep it running in background.</div>
                          <div>5. Receipts can now auto print.</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeSettingsTab === "customization" && (
                  <Customization
                    userID={userID}
                    active={activeMainTab === "settings" && activeSettingsTab === "customization"}
                  />
                )}

                {activeSettingsTab === "paymentmethod" && (
                  <OtherPaymentMethod
                    userID={userID}
                    active={activeMainTab === "settings" && activeSettingsTab === "paymentmethod"}
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
